import Layout from '../components/Layout';
import { ShieldCheck, Phone, Award, MapPin, Sparkles, ParkingSquare, LifeBuoy, Bell, Gauge, Lock, FileText } from 'lucide-react';

const STATS = [
  { icon: Award, k: '5K+', v: 'Tags sold' },
  { icon: Phone, k: '100K+', v: 'Masked calls routed' },
  { icon: MapPin, k: '100+', v: 'Cities served' },
  { icon: ShieldCheck, k: '4.7\u2605', v: 'Owner rating' },
];

const VISION_POINTS = [
  { icon: ParkingSquare, title: 'Parking issues', desc: 'Instantly reach owners of wrongly-parked vehicles without ever revealing anyone\u2019s number.' },
  { icon: Lock, title: 'Vehicle safety', desc: 'A tamper-proof QR shield linked to a masked layer keeps your identity safe on every scan.' },
  { icon: LifeBuoy, title: 'Emergency RSA help', desc: 'One tap raises a roadside assistance ticket — towing, jump-start, fuel or flat tyre.' },
  { icon: Bell, title: 'Smart reminders', desc: 'Never miss a PUC, insurance renewal or service date. We nudge you at the right time.' },
  { icon: Gauge, title: 'Fuel tracking', desc: 'Log every fill-up and let the AI-style tracker learn your mileage and cost patterns.' },
  { icon: FileText, title: 'Insurance claim support', desc: 'Hassle-free guidance on filing motor insurance claims — document checklist, garage tie-ups and follow-up help when you need it most.' },
];

export default function AboutPage() {
  return (
    <Layout>
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="eyebrow mb-4">About us</p>
          <h1 className="h-display text-5xl md:text-6xl">A simple idea. <em className="font-serif-display italic font-normal text-navy">Privacy for every owner.</em></h1>
          <p className="mt-6 text-lg text-neutral-600">Connect Near Me is a venture of Explore Biz. We build privacy-first QR tags and a digital wallet for Indian vehicle owners. One waterproof tag on your bike or car keeps you reachable in emergencies — without ever revealing your number.</p>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-16 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="about-stats">
          {STATS.map((s) => (
            <div key={s.v} className="card-soft p-6 text-center" data-testid={`stat-${s.v.replace(/\s+/g, '-').toLowerCase()}`}>
              <s.icon size={22} className="mx-auto text-navy" />
              <p className="h-display text-3xl mt-3">{s.k}</p>
              <p className="text-sm text-neutral-500 mt-1">{s.v}</p>
            </div>
          ))}
        </div>

        {/* Vision */}
        <div className="max-w-5xl mx-auto px-6 mt-24" data-testid="vision-section">
          <div className="flex items-center gap-2 mb-4"><Sparkles size={16} className="text-orange-500" /><p className="eyebrow">Our Vision</p></div>
          <h2 className="h-display text-3xl md:text-4xl leading-tight">A safer, calmer commute for every Indian on the road.</h2>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
            Roads are getting narrower even as the number of vehicles keeps climbing. Improper parking, blocked driveways, breakdowns in the middle of a highway, missed insurance renewals — every driver knows this stress. Our digital solution gives vehicle owners <span className="font-semibold text-navy">complete peace of mind</span> — a single QR tag that quietly takes care of the five things that eat into your day:
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VISION_POINTS.map((v) => (
              <div key={v.title} className="card-soft p-5" data-testid={`vision-point-${v.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="w-10 h-10 rounded-lg bg-navy text-white flex items-center justify-center mb-3"><v.icon size={16} /></div>
                <p className="font-semibold">{v.title}</p>
                <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mission */}
        <div className="max-w-5xl mx-auto px-6 mt-24 card-soft p-8 md:p-12" style={{background: 'linear-gradient(135deg, #0B2E6F 0%, #071E4A 60%, #030A1F 100%)'}} data-testid="mission-section">
          <div className="text-white">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-orange-300" /><p className="text-xs uppercase tracking-[0.2em] text-blue-200 font-semibold">Our Mission</p></div>
            <h2 className="h-display text-3xl md:text-4xl leading-tight">Accessible digital vehicle management, road safety and instant emergency support — for every corner of India.</h2>
            <p className="mt-5 text-blue-100 text-lg leading-relaxed">
              We are on a mission to make world-class vehicle safety technology as ordinary as a helmet — affordable, tamper-proof and available in every pin code. Whether you drive a scooter in a small town or a car in a metro, Connect Near Me will be your invisible co-driver: shielding your identity, guiding help to you in seconds, and keeping every document and reminder in one clean digital wallet.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-white/10 rounded-lg p-3"><p className="font-semibold text-orange-300">Safety-first</p><p className="text-xs text-blue-200 mt-1">Masked calling, verified RSA vendors, encrypted wallet.</p></div>
              <div className="bg-white/10 rounded-lg p-3"><p className="font-semibold text-orange-300">Pan-India</p><p className="text-xs text-blue-200 mt-1">Ships everywhere. RSA network across 100+ cities.</p></div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
